import React, { useCallback, useEffect, useMemo } from 'react'
import SearchBar from './SearchBar'
import { MIN_SEARCH_CHARS } from 'constants/global'
import styles from './styles/player-search-bar.module.scss'
import i18n from 'constants/strings'
import { useDispatch, useSelector } from 'react-redux'
import {
  suggestPlayers,
  setSearchQuery,
  clearSuggestions,
  setSorting,
} from 'actions/search-actions'
import { AppState } from 'redux/reducers'
import { getProfileUrl, getQueryParam } from 'utils'
import { getWebRootUrl } from 'constants/api'
import PlayerSuggestion from './PlayerSuggestion'
import useWatchlists from 'hooks/use-watchlists'
import { DEFAULT_TABLE_SORTING } from 'constants/player-search'

/* redirectOnViewAll Prop value optionally passed from HTML elements
 see 'src/public/index' and 'src/widgets/registerWidget' */
interface PlayerSearchBarProps {
  searchId: string
  redirectOnViewAll?: boolean
  viewAllEnabled?: boolean
  noSuggestionsExtraInfo?: string
  shouldSearchOnLoad?: boolean
  removeSortingOnSearch?: boolean
}

const PlayerSearchBar: React.FC<PlayerSearchBarProps> = ({
  searchId,
  redirectOnViewAll,
  viewAllEnabled,
  noSuggestionsExtraInfo,
  shouldSearchOnLoad = false,
  removeSortingOnSearch = false,
}) => {
  const dispatch = useDispatch()
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const suggestions = useSelector((state: AppState) => state.searchbar.suggestions[searchId])
  const waitingForSuggestions = useSelector(
    (state: AppState) => state.searchbar.suggestionsPending[searchId]
  )
  const { isPlayerWatched } = useWatchlists()
  const getSuggestions = useCallback(
    search => {
      dispatch(suggestPlayers(search, searchId))
    },
    [dispatch, searchId]
  )
  const resetSearch = useCallback(() => dispatch(clearSuggestions(searchId)), [dispatch, searchId])
  const onSearch = useCallback(
    search => {
      if (removeSortingOnSearch)
        dispatch(setSorting(search ? null : DEFAULT_TABLE_SORTING, searchId))

      dispatch(setSearchQuery(search, searchId))
    },
    [dispatch, searchId, removeSortingOnSearch]
  )
  const initialSearchString = useMemo(() => getQueryParam('search'), [])

  useEffect(() => {
    resetSearch()
  }, [resetSearch])

  return (
    <SearchBar
      shouldSearchOnLoad={shouldSearchOnLoad}
      minCharsToGetSuggestions={MIN_SEARCH_CHARS}
      initialSearchString={initialSearchString}
      redirectOnViewAll={redirectOnViewAll}
      viewAllEnabled={viewAllEnabled}
      onFinishedTyping={getSuggestions}
      loading={waitingForSuggestions}
      onSearch={onSearch}
      onEnterKey={suggestion => {
        if (suggestion) {
          window.location.href = getWebRootUrl() + getProfileUrl(suggestion?.id)
        }
      }}
      suggestions={suggestions}
      suggestionType={i18n.t('strSearchBar.player')}
      suggestionTypes={i18n.t('strSearchBar.players')}
      renderSuggestion={suggestion => (
        <a className={styles.suggestion} href={getProfileUrl(suggestion.id)}>
          <PlayerSuggestion
            player={suggestion}
            showWatchlistStatus={isLoggedIn}
            isInWatchlists={isPlayerWatched(suggestion.id)}
          />
        </a>
      )}
      noSuggestionsExtraInfo={noSuggestionsExtraInfo}
      classes={{ suggestionsWrapper: styles.searchWrapper }}
    />
  )
}

export default PlayerSearchBar
